import React, { FormEvent, useMemo } from 'react';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { AccordionIndicator, AccordionOpenCloseIcon } from '../../custom/AccordionIndicators';
import SarInfoRow from './SarInfoRow';
import SarValidationCategoryMapper from '../../../util/SarValidationCategoryMapper';
import { getAciliyetOptions, getBildirimKategorisiOptions, getTerorOrgutuOptions } from '../../../constant/SarTagConstants';
import { SarValidationResult } from '../SarInterfaces';

const TemelBilgilerSection = ({
  xmlContent,
  bildirimKategorisi,
  aciliyet,
  terorOrgutu,
  yerelReferansNoFallback,
  oncekiBildirimReferansNoFallback,
  onSelectBildirimKategorisi,
  onChangeYerelReferansNo,
  onChangeOncekiBildirimReferansNo,
  onSelectAciliyet,
  onSelectTerorOrgutu,
  validate,
}: Props) => {
  const invalidFieldIds = useMemo(() => {
    if (!validate || !validate.errors || validate.errors.length === 0) {
      return new Set<string>();
    }
    return SarValidationCategoryMapper.getInvalidFieldIds(validate.errors);
  }, [validate]);

  const hasBildirimKategorisiError = useMemo(() => {
    return invalidFieldIds.has('bildirimkategorisi');
  }, [invalidFieldIds]);

  const hasYerelReferansNoError = useMemo(() => {
    return invalidFieldIds.has('yerelreferansno');
  }, [invalidFieldIds]);

  const hasOncekiBildirimReferansNoError = useMemo(() => {
    return invalidFieldIds.has('oncekibildirimreferansno');
  }, [invalidFieldIds]);

  const hasAciliyetError = useMemo(() => {
    return invalidFieldIds.has('aciliyet');
  }, [invalidFieldIds]);

  const hasTerorOrgutuError = useMemo(() => {
    return invalidFieldIds.has('terororgutu');
  }, [invalidFieldIds]);

  const hasAnyError = useMemo(() => {
    return hasBildirimKategorisiError || hasYerelReferansNoError || hasOncekiBildirimReferansNoError || hasAciliyetError || hasTerorOrgutuError;
  }, [hasBildirimKategorisiError, hasYerelReferansNoError, hasOncekiBildirimReferansNoError, hasAciliyetError, hasTerorOrgutuError]);

  const { yerelReferansNo, oncekiBildirimReferansNo } = useMemo(() => {
    if (!xmlContent || xmlContent.trim().length === 0) {
      return {
        yerelReferansNo: yerelReferansNoFallback,
        oncekiBildirimReferansNo: oncekiBildirimReferansNoFallback,
      };
    }
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    const yerelReferansNoElement = xmlDoc.getElementsByTagName('yerelreferansno')[0];
    const oncekiBildirimReferansNoElement = xmlDoc.getElementsByTagName('oncekibildirimreferansno')[0];
    return {
      yerelReferansNo: yerelReferansNoElement && yerelReferansNoElement.textContent ? yerelReferansNoElement.textContent : yerelReferansNoFallback,
      oncekiBildirimReferansNo:
        oncekiBildirimReferansNoElement && oncekiBildirimReferansNoElement.textContent
          ? oncekiBildirimReferansNoElement.textContent
          : oncekiBildirimReferansNoFallback,
    };
  }, [xmlContent, yerelReferansNoFallback, oncekiBildirimReferansNoFallback]);

  return (
    <div className={'h-full w-full pr-6 pt-3 2xl:pt-4 flex flex-col gap-y-3'}>
      <div className={'text-[#1D2939] text-base font-semibold'}>XML Document</div>
      <div className={'flex flex-col border rounded-[10px] h-full overflow-y-auto overflow-hidden'}>
        <div className={'flex w-full items-center justify-between px-5 2xl:px-6 h-[56px] 2xl:h-[58px] text-left bg-white border-b border-[#F2F4F7]'}>
          <div className={'flex items-center gap-x-3'}>
            <AccordionOpenCloseIcon expanded={true} hasError={hasAnyError} />
            <div className={'flex flex-col'}>
              <span className={`text-xs uppercase tracking-wide ${hasAnyError ? 'text-f-red-1' : 'text-[#667085]'}`}>Information</span>
              <span className={`text-sm font-semibold ${hasAnyError ? 'text-f-red-1' : 'text-[#1D2939]'}`}>Temel Bilgiler</span>
            </div>
          </div>
          <AccordionIndicator expanded={true} />
        </div>
        <div className={'flex flex-col'}>
          <SarInfoRow label={'Bildirim Kategorisi'} hasError={hasBildirimKategorisiError} infoConfig={{ path: 'temelBilgi.bildirimKategorisi', type: 'Kod' }}>
            <div className={'w-full max-w-[320px]'}>
              <FormicaSearchSelectBox
                ghost={true}
                options={getBildirimKategorisiOptions()}
                rounded
                value={bildirimKategorisi}
                placeholder={'Bildirim Kategorisi'}
                onChange={onSelectBildirimKategorisi}
                className={hasBildirimKategorisiError ? 'placeholder:!text-f-red-1 !text-f-red-1' : ''}
                selectBoxClassName={hasBildirimKategorisiError ? 'border-f-red-1 border' : ''}
              />
            </div>
          </SarInfoRow>
          <SarInfoRow label={'Yerel Referans No'} hasSeparator hasError={hasYerelReferansNoError} infoConfig={{ path: 'temelBilgi.yerelReferansNo' }}>
            <input
              className={`w-full text-right focus:outline-none h-[34px] min-h-[34px] 2xl:h-[36px] 2xl:min-h-[36px] text-sm indent-1 text-f-black-2 bg-white pr-2 ${hasYerelReferansNoError ? 'border-f-red-1 border rounded-[4px] placeholder:text-f-red-1 text-f-red-1' : ''}`}
              value={yerelReferansNo}
              onChange={onChangeYerelReferansNo}
              placeholder={'Yerel Referans No Giriniz...'}
            />
          </SarInfoRow>
          <SarInfoRow
            label={'Önceki Bildirim Referans No'}
            hasSeparator
            hasError={hasOncekiBildirimReferansNoError}
            infoConfig={{ path: 'temelBilgi.oncekiBildirimReferansNo' }}
          >
            <input
              className={`w-full text-right focus:outline-none h-[34px] min-h-[34px] 2xl:h-[36px] 2xl:min-h-[36px] text-sm indent-1 text-f-black-2 bg-white pr-2 ${hasOncekiBildirimReferansNoError ? 'border-f-red-1 border rounded-[4px] placeholder:text-f-red-1 text-f-red-1' : ''}`}
              value={oncekiBildirimReferansNo}
              onChange={onChangeOncekiBildirimReferansNo}
              placeholder={'Önceki Bildirim Referans No Giriniz...'}
            />
          </SarInfoRow>
          <SarInfoRow label={'Aciliyet'} hasSeparator hasError={hasAciliyetError} infoConfig={{ path: 'temelBilgi.aciliyet', type: 'Kod' }}>
            <div className={'w-full max-w-[320px]'}>
              <FormicaSearchSelectBox
                ghost={true}
                options={getAciliyetOptions()}
                value={aciliyet}
                rounded
                placeholder={'Aciliyet'}
                onChange={onSelectAciliyet}
                className={hasAciliyetError ? 'placeholder:!text-f-red-1 !text-f-red-1' : ''}
                selectBoxClassName={hasAciliyetError ? 'border-f-red-1 border' : ''}
              />
            </div>
          </SarInfoRow>
          <SarInfoRow label={'Terör Örgütü'} hasSeparator hasError={hasTerorOrgutuError} infoConfig={{ path: 'temelBilgi.terorOrgutu', type: 'Kod' }}>
            <div className={'w-full max-w-[320px]'}>
              <FormicaSearchSelectBox
                ghost={true}
                options={getTerorOrgutuOptions()}
                value={terorOrgutu}
                rounded
                placeholder={'Terör Örgütü'}
                onChange={onSelectTerorOrgutu}
                className={hasTerorOrgutuError ? 'placeholder:!text-f-red-1 !text-f-red-1' : ''}
                selectBoxClassName={hasTerorOrgutuError ? 'border-f-red-1 border' : ''}
              />
            </div>
          </SarInfoRow>
        </div>
      </div>
    </div>
  );
};

export interface Props {
  xmlContent?: string;
  bildirimKategorisi: string;
  aciliyet: string;
  terorOrgutu: string;
  yerelReferansNoFallback: string;
  oncekiBildirimReferansNoFallback: string;
  onSelectBildirimKategorisi: (item: FormicaSelectBoxItem) => void;
  onChangeYerelReferansNo: (event: FormEvent<HTMLInputElement>) => void;
  onChangeOncekiBildirimReferansNo: (event: FormEvent<HTMLInputElement>) => void;
  onSelectAciliyet: (item: FormicaSelectBoxItem) => void;
  onSelectTerorOrgutu: (item: FormicaSelectBoxItem) => void;
  validate?: SarValidationResult;
}

export default TemelBilgilerSection;
